import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Search, Download, ChevronLeft, ChevronRight, SlidersHorizontal } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function UpstreamError({ detail }) {
  return (
    <div data-testid="upstream-error" style={{
      background: '#fef9ec', border: '1px solid #fde68a', borderRadius: 8,
      padding: '20px 24px', margin: '24px 0',
    }}>
      <div style={{ fontWeight: 700, color: '#92400e', marginBottom: 6, fontSize: 14 }}>
        Upstream API Unavailable
      </div>
      <div style={{ color: '#78350f', fontSize: 13 }}>{detail}</div>
      <div style={{ color: '#92400e', fontSize: 12, marginTop: 8 }}>
        This page requires a lupnxtlaravel account. Contact your administrator or log in with credentials that have access.
      </div>
    </div>
  );
}

function StatusPill({ value }) {
  const active = String(value).toLowerCase() === 'active' || value === 1 || value === true;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 20,
      fontSize: 11, fontWeight: 600,
      background: active ? '#dcfce7' : '#f1f5f9',
      color: active ? '#15803d' : '#64748b',
    }}>
      {active ? 'Active' : 'Inactive'}
    </span>
  );
}

function exportCSV(rows, filename) {
  if (!rows.length) return;
  const keys = Object.keys(rows[0]);
  const lines = [
    keys.join(','),
    ...rows.map(r => keys.map(k => {
      const v = r[k] ?? '';
      return String(v).includes(',') ? `"${v}"` : v;
    }).join(',')),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function FilterSelect({ testId, value, onChange, placeholder, options }) {
  return (
    <select
      data-testid={testId}
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{
        height: 32, padding: '0 10px', fontSize: 12, border: '1px solid #e2e8f0',
        borderRadius: 6, background: value ? '#f0fdf4' : '#fff',
        color: value ? '#15803d' : '#64748b', cursor: 'pointer', outline: 'none',
        maxWidth: 200,
      }}
    >
      <option value="">{placeholder}</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

export default function LearnersAdmin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [upstreamError, setUpstreamError] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState(null);

  // Filters
  const [filterCountry, setFilterCountry] = useState('');
  const [filterRegion, setFilterRegion] = useState('');
  const [filterPartner, setFilterPartner] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('');
  const [filterOptions, setFilterOptions] = useState({ countries: [], regions: [], platforms: [] });

  const PER_PAGE = 50;

  // Load filter options once
  useEffect(() => {
    axios.get(`${API}/api/lup/learners/filter-options`, { withCredentials: true })
      .then(res => setFilterOptions(res.data))
      .catch(() => {});
  }, []);

  const fetchData = useCallback(async (pg = 1, q = '', filters = {}) => {
    setLoading(true);
    setError(null);
    setUpstreamError(null);
    try {
      const params = { page: pg, per_page: PER_PAGE };
      if (q) params.search = q;
      if (filters.country) params.country = filters.country;
      if (filters.region) params.region = filters.region;
      if (filters.partner) params.partner = filters.partner;
      if (filters.platform) params.platform = filters.platform;
      const res = await axios.get(`${API}/api/lup/learners`, { params, withCredentials: true });
      const data = res.data;
      if (Array.isArray(data)) {
        setRows(data);
        setMeta(null);
      } else if (data?.data) {
        setRows(data.data);
        setMeta(data.meta || null);
      } else {
        setRows([]);
      }
    } catch (e) {
      if (e.response?.status === 503) {
        setUpstreamError(e.response.data?.detail || 'Not authenticated with upstream API.');
      } else {
        setError(e.response?.data?.detail || 'Failed to load learner data.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(1, search, { country: filterCountry, region: filterRegion, partner: filterPartner, platform: filterPlatform }); }, []);

  const activeFilters = { country: filterCountry, region: filterRegion, partner: filterPartner, platform: filterPlatform };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchData(1, search, activeFilters);
  };

  const handleFilterChange = (key, value) => {
    const next = { ...activeFilters, [key]: value };
    if (key === 'country') { next.country = value; setFilterCountry(value); }
    if (key === 'region') { next.region = value; setFilterRegion(value); }
    if (key === 'partner') { next.partner = value; setFilterPartner(value); }
    if (key === 'platform') { next.platform = value; setFilterPlatform(value); }
    setPage(1);
    fetchData(1, search, next);
  };

  const clearFilters = () => {
    setFilterCountry(''); setFilterRegion(''); setFilterPartner(''); setFilterPlatform('');
    setSearch(''); setPage(1);
    fetchData(1, '', {});
  };

  const hasActiveFilters = filterCountry || filterRegion || filterPartner || filterPlatform;

  const handlePage = (newPage) => {
    setPage(newPage);
    fetchData(newPage, search, activeFilters);
  };

  const COLUMNS = useMemo(() => {
    if (!rows.length) return [];
    const preferred = [
      { key: 'id',              label: 'LMS ID' },
      {
        key: '_name',
        label: 'Name',
        maxWidth: 200,
        render: (_, row) => `${row.first_name || ''} ${row.last_name || ''}`.trim() || '—',
      },
      { key: 'email',           label: 'Email',     maxWidth: 240 },
      { key: 'country',         label: 'Country' },
      { key: 'region',          label: 'Region' },
      { key: 'partner_name',    label: 'Partner',   maxWidth: 200 },
      {
        key: 'verified',
        label: 'Status',
        render: v => <StatusPill value={v} />,
      },
      { key: 'enrollments_count', label: 'Enrollments', align: 'right' },
      { key: 'platform',          label: 'Platform',    maxWidth: 180 },
      { key: 'platform_user_id',  label: 'Platform User ID', maxWidth: 220 },
    ];
    const available = new Set(Object.keys(rows[0]));
    // Always include virtual _name column if first_name exists
    // platform and platform_user_id are always injected by backend (may be null)
    return preferred.filter(c => {
      if (c.key === '_name') return available.has('first_name');
      if (c.key === 'platform' || c.key === 'platform_user_id') return true;
      return available.has(c.key);
    });
  }, [rows]);

  const lastPage = meta?.last_page || (meta?.total ? Math.ceil(meta.total / PER_PAGE) : null);

  return (
    <div data-testid="learners-admin" style={{ padding: '0 16px' }}>
      <div style={{ padding: '20px 0 12px' }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: 0 }}>Learners Admin</h1>
        <p style={{ fontSize: 13, color: '#64748b', margin: '4px 0 0' }}>
          Manage and view all learner accounts via lupnxtlaravel.
        </p>
      </div>

      {upstreamError && <UpstreamError detail={upstreamError} />}

      {!upstreamError && (
        <>
          {/* Toolbar */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
            <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8 }}>
              <div style={{ position: 'relative' }}>
                <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                <input
                  data-testid="learner-search"
                  type="text"
                  placeholder="Search name or email…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  style={{ height: 34, paddingLeft: 32, paddingRight: 12, fontSize: 13, border: '1px solid #e2e8f0', borderRadius: 7, outline: 'none', width: 260 }}
                />
              </div>
              <button type="submit" style={{ height: 34, padding: '0 16px', fontSize: 13, fontWeight: 600, background: '#194449', color: '#fff', border: 'none', borderRadius: 7, cursor: 'pointer' }}>
                Search
              </button>
            </form>
            <button
              data-testid="learner-export-btn"
              onClick={() => exportCSV(rows, 'learners.csv')}
              disabled={!rows.length}
              style={{ height: 34, padding: '0 14px', fontSize: 13, fontWeight: 600, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 7, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <Download size={14} /> Export CSV
            </button>
          </div>

          {/* Filters */}
          <div data-testid="learner-filters" style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <SlidersHorizontal size={14} style={{ color: '#64748b', flexShrink: 0 }} />
            <FilterSelect
              testId="filter-region"
              value={filterRegion}
              onChange={v => handleFilterChange('region', v)}
              placeholder="All Regions"
              options={filterOptions.regions}
            />
            <FilterSelect
              testId="filter-country"
              value={filterCountry}
              onChange={v => handleFilterChange('country', v)}
              placeholder="All Countries"
              options={filterOptions.countries}
            />
            {/* Partner: text input since there are 1600+ partners */}
            <form onSubmit={e => { e.preventDefault(); handleFilterChange('partner', filterPartner); }} style={{ display: 'flex', gap: 4 }}>
              <input
                data-testid="filter-partner"
                type="text"
                placeholder="Filter by partner…"
                value={filterPartner}
                onChange={e => setFilterPartner(e.target.value)}
                onBlur={() => handleFilterChange('partner', filterPartner)}
                style={{ height: 32, padding: '0 10px', fontSize: 12, border: '1px solid #e2e8f0', borderRadius: 6, outline: 'none', width: 180, background: filterPartner ? '#f0fdf4' : '#fff', color: filterPartner ? '#15803d' : '#374151' }}
              />
            </form>
            <FilterSelect
              testId="filter-platform"
              value={filterPlatform}
              onChange={v => handleFilterChange('platform', v)}
              placeholder="All Platforms"
              options={filterOptions.platforms}
            />
            {hasActiveFilters && (
              <button
                data-testid="clear-filters-btn"
                onClick={clearFilters}
                style={{ height: 32, padding: '0 12px', fontSize: 12, fontWeight: 600, background: '#fff', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 6, cursor: 'pointer' }}
              >
                Clear filters
              </button>
            )}
          </div>

          {/* Table */}
          <div className="report-card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table className="report-table" data-testid="learners-table">
                <thead>
                  <tr>
                    {COLUMNS.map(c => (
                      <th key={c.key} style={{ textAlign: c.align === 'right' ? 'right' : 'left' }}>{c.label}</th>
                    ))}
                    {!COLUMNS.length && <th>Loading columns…</th>}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={COLUMNS.length || 1} style={{ textAlign: 'center', color: '#94a3b8', padding: '32px 0' }}>Loading…</td></tr>
                  ) : error ? (
                    <tr><td colSpan={COLUMNS.length || 1} style={{ textAlign: 'center', color: '#dc2626', padding: '24px 0', fontSize: 13 }}>{error}</td></tr>
                  ) : rows.length === 0 ? (
                    <tr><td colSpan={COLUMNS.length || 1} style={{ textAlign: 'center', color: '#94a3b8', padding: '24px 0' }}>No learners found.</td></tr>
                  ) : (
                    rows.map((row, i) => (
                      <tr key={row.id ?? i} data-testid={`learner-row-${row.id ?? i}`}>
                        {COLUMNS.map(c => (
                          <td key={c.key} style={{ textAlign: c.align === 'right' ? 'right' : 'left', fontSize: 13, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {c.render ? c.render(row[c.key], row) : (row[c.key] ?? '—')}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {lastPage && lastPage > 1 && (
            <div data-testid="learner-pagination" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 13 }}>
              <span style={{ color: '#64748b' }}>
                Page {page} of {lastPage}{meta?.total ? ` (${meta.total.toLocaleString()} total)` : ''}
              </span>
              <button onClick={() => handlePage(page - 1)} disabled={page <= 1} style={{ width: 32, height: 32, border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', cursor: page > 1 ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ChevronLeft size={14} />
              </button>
              <button onClick={() => handlePage(page + 1)} disabled={page >= lastPage} style={{ width: 32, height: 32, border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', cursor: page < lastPage ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ChevronRight size={14} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
